import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Card, Badge, Avatar, Button, Spinner, Alert } from '@opendate-app/setlist';
const qs = require('qs');

import { BOARD_STAGES, grantStageConfig, nextDeadlineText, toAmount } from '../grantStages';
import { formatMoney } from '../../shared/utils/crmMoney';
import { initialsFor } from '../../Deals/components/dealStages';

const AWARDED_STAGES = ['awarded', 'complete'];
const CLOSED_STAGES = ['declined', 'complete'];

const cardAmount = (grant) => {
  const requested = toAmount(grant.requested_amount) || 0;
  if (!AWARDED_STAGES.includes(grant.stage)) return requested;
  return toAmount(grant.awarded_amount) || requested;
};

const deadlineLine = (grant) => {
  if (!grant.next_deadline && CLOSED_STAGES.includes(grant.stage)) return 'Closed';
  return nextDeadlineText(grant.next_deadline).line;
};

const flexRow = { display: 'flex', alignItems: 'center', gap: 'var(--setlist-spacing-md)' };
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function GrantCard({ team, grant, dragging }) {
  return (
    <Card padding="md" style={{ cursor: 'grab', opacity: dragging ? 0.4 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-sm)' }}>
        <div style={{ ...flexRow, justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Button
            as="a"
            variant="link-color"
            size="sm"
            href={`/teams/${team.id}/crm/grants/${grant.id}`}
            draggable={false}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {grant.name}
          </Button>
          <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatMoney(cardAmount(grant)) || '$0'}</span>
        </div>
        {grant.funder && <div style={{ ...ellipsis, color: 'var(--sl-text-secondary)' }}>{grant.funder}</div>}
        {grant.organization_name && (
          <div style={{ ...ellipsis, color: 'var(--sl-text-tertiary)' }}>{grant.organization_name}</div>
        )}
        <div style={{ ...flexRow, justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--sl-text-tertiary)' }}>{deadlineLine(grant)}</span>
          {grant.owner_name && <Avatar size="xs" initials={initialsFor(grant.owner_name)} alt={grant.owner_name} title={grant.owner_name} />}
        </div>
      </div>
    </Card>
  );
}

function DraggableGrantCard(props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.grant.id,
    data: { stage: props.grant.stage },
  });

  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={{ marginBottom: 'var(--setlist-spacing-md)' }}>
      <GrantCard {...props} dragging={isDragging} />
    </div>
  );
}

function PipelineColumn({ team, stage, grants }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const { label, color, subtitle } = grantStageConfig(stage);
  const columnTotal = grants.reduce((sum, g) => sum + cardAmount(g), 0);

  return (
    <div style={{ flex: '0 0 300px', width: 300, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 var(--setlist-spacing-xs) var(--setlist-spacing-md)' }}>
        <div style={flexRow}>
          <Badge size="sm" color={color} dot>{label}</Badge>
          <span style={{ color: 'var(--sl-text-tertiary)' }}>{grants.length}</span>
        </div>
        <div style={{ ...flexRow, justifyContent: 'space-between', color: 'var(--sl-text-tertiary)' }}>
          <span>{subtitle}</span>
          <span style={{ fontWeight: 600 }}>{formatMoney(columnTotal) || '$0'}</span>
        </div>
      </div>

      <div
        ref={setNodeRef}
        style={{
          flex: 1,
          minHeight: 120,
          borderRadius: 'var(--setlist-radius-lg)',
          padding: 'var(--setlist-spacing-md)',
          background: isOver ? 'var(--sl-bg-secondary)' : 'var(--sl-bg-tertiary)',
          border: `1px dashed ${isOver ? 'var(--sl-border-primary)' : 'transparent'}`,
        }}
      >
        {grants.map((grant) => (
          <DraggableGrantCard key={grant.id} team={team} grant={grant} />
        ))}
        {grants.length === 0 && (
          <div style={{ color: 'var(--sl-text-quaternary)', textAlign: 'center', padding: 'var(--setlist-spacing-xl) 0' }}>
            No grants
          </div>
        )}
      </div>
    </div>
  );
}

function GrantsPipelineBoard({ team, csrfToken, q, due, refreshKey = 0, onLoaded, onMoved }) {
  const [grantsByStage, setGrantsByStage] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [activeGrant, setActiveGrant] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const requestRef = useRef(0);
  const onLoadedRef = useRef(onLoaded);
  const onMovedRef = useRef(onMoved);
  onLoadedRef.current = onLoaded;
  onMovedRef.current = onMoved;
  const qKey = JSON.stringify(q || {});

  const fetchBoard = useCallback(async () => {
    if (!team || !team.id) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    try {
      setError(null);
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const params = { view: 'board', q };
      if (due) params.due = due;

      const response = await axios.get(`/teams/${team.id}/crm/grants.json`, {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
      });
      if (requestId !== requestRef.current) return;

      const byStage = response.data.grants_by_stage || {};
      setGrantsByStage(byStage);
      setLoaded(true);
      if (onLoadedRef.current) {
        onLoadedRef.current({
          summary: response.data.summary,
          funders: response.data.funders,
          count: BOARD_STAGES.reduce((sum, stage) => sum + (byStage[stage] || []).length, 0),
        });
      }
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setError('Failed to load grants');
    }
  }, [team && team.id, csrfToken, qKey, due]);

  const fetchBoardRef = useRef(fetchBoard);
  useEffect(() => {
    fetchBoardRef.current = fetchBoard;
  }, [fetchBoard]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard, refreshKey]);

  const findGrant = (id) => {
    for (const stage of Object.keys(grantsByStage)) {
      const match = (grantsByStage[stage] || []).find((g) => g.id === id);
      if (match) return { grant: match, stage };
    }
    return null;
  };

  const handleDragStart = ({ active }) => {
    const found = findGrant(active.id);
    setActiveGrant(found ? found.grant : null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveGrant(null);
    if (!over) return;

    const found = findGrant(active.id);
    if (!found) return;

    const fromStage = found.stage;
    const toStage = over.id;
    if (fromStage === toStage) return;

    const moved = { ...found.grant, stage: toStage };

    setGrantsByStage((prev) => ({
      ...prev,
      [fromStage]: (prev[fromStage] || []).filter((g) => g.id !== moved.id),
      [toStage]: [moved, ...(prev[toStage] || [])],
    }));

    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.patch(`/teams/${team.id}/crm/grants/${moved.id}.json`, { grant: { stage: toStage } });
      if (onMovedRef.current) onMovedRef.current();
    } catch (e) {
      toast.error('Failed to move grant');
      fetchBoardRef.current();
    }
  };

  if (error) {
    return (
      <div style={{ padding: 'var(--setlist-spacing-2xl)' }}>
        <Alert color="error" title="Failed to load grants" description={error} />
      </div>
    );
  }

  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--setlist-spacing-2xl)' }}>
        <Spinner label="Loading grants…" />
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', gap: 'var(--setlist-spacing-lg)', overflowX: 'auto', paddingBottom: 'var(--setlist-spacing-md)', alignItems: 'stretch' }}>
        {BOARD_STAGES.map((stage) => (
          <PipelineColumn key={stage} team={team} stage={stage} grants={grantsByStage[stage] || []} />
        ))}
      </div>

      <DragOverlay>
        {activeGrant ? (
          <div style={{ width: 284 }}>
            <GrantCard team={team} grant={activeGrant} dragging={false} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default GrantsPipelineBoard;
